import { ReactNode, useEffect, useState } from 'react';
import logger from '@logger';
import { useAppDispatch } from '@hooks/useAppDispatch';
import trackCompletedSection from '@helpers/analyticsHelpers/trackCompletedSection';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { StyledCheckoutSubmitButton } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { CheckoutError, createKlarnaSession, placeOrder } from '@api/interfaces/checkoutApi';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import { setCompleted, setEditing, setErrorMessage, setSelectedMode } from '@slices/checkoutSlice';
import paths from '@constants/paths';
import { useRouter } from 'next/navigation';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import ProcessingOrderModal from '@molecules/CheckoutPaymentButton/ProcessingOrderModal/ProcessingOrderModal';
import useAbortController from '@hooks/useAbortController';
import { setWindowHref } from '@helpers/setWindowHref';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
  setError: (error: ErrorDataType) => void;
}

const KlarnaButton = ({ disabled = false, possiblyWrongStoreConfirmed, children, setError }: Props) => {
  const [authorizeToken, setAuthorizeToken] = useState<string>('');
  const [klarnaDisabled, setKlarnaDisabled] = useState<boolean>(disabled);
  const [klarnaLoading, setKlarnaLoading] = useState<boolean>(false);
  const [showProcessingOrderModal, setShowProcessingOrderModal] = useState<boolean>(false);

  const { cart } = useCart();
  const { store } = useStore();

  const checkCartConflict = useCheckCartConflict({ checkStock: true });
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { getSignal } = useAbortController();

  const authorizeKlarna = async () => {
    setKlarnaLoading(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });

      const { data } = await createKlarnaSession();
      window?.Klarna?.Payments.init({ client_token: data.client_token });

      window?.Klarna?.Payments.authorize({}, (res: any) => {
        setKlarnaLoading(false);

        if (res.show_form && res.approved) {
          setAuthorizeToken(res.authorization_token);
        } else {
          setAuthorizeToken('');
        }
      });
    } catch (e) {
      setKlarnaLoading(false);
      setAuthorizeToken('');
      if (e instanceof Error) {
        logger.error({ error: 'Unauthorized Klarna payment', message: e.message });
      }
    }
  };

  const submitOrder = async () => {
    setKlarnaLoading(true);
    setShowProcessingOrderModal(true);
    try {
      if (cart?.orderReference) {
        // Do conflict check only if order is open and being changed, as we then skip authorizeKlarna
        await checkCartConflict({ storeId: store?.storeId });
      }

      const location = await placeOrder(
        {
          orderRef: cart?.orderReference,
          selectedCardId: '',
          saveCard: false,
          userClickedContinue:
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL ||
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY,
          klarnaAuthorizationToken: authorizeToken,
        },
        getSignal()
      );
      trackCompletedSection('payment');
      // @ts-ignore
      setWindowHref(location);
    } catch (error) {
      setKlarnaLoading(false);
      setShowProcessingOrderModal(false);
      setAuthorizeToken('');

      if (error instanceof CheckoutError) {
        pushGTMEvent({
          event: 'Track',
          category: 'klarna_error',
          action: `error_message_shown`,
          label: `${error.message} ${error.details}`,
          value: 0,
        });

        if (error.details === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
          dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
          dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
          dispatch(setErrorMessage(error.details));
          setError({ errorCode: error.details });
        } else if (error.details === PlaceOrderErrorResponse.EMPTY_CART) {
          dispatch(setEmptyCartModalVisibility(true));
          router.push(paths.HOME);
        } else {
          setError({ errorCode: error.details || 'default' });
          dispatch(setSelectedMode(''));
          logger.error({ error: 'klarna error', message: error.details });
        }
      } else {
        setError({ errorCode: 'default' });
        dispatch(setSelectedMode(''));
        logger.error({ error: 'klarna error', message: error instanceof Error ? error.message : 'Unknown error' });
      }
    }
  };

  const submitPayment = async () => {
    trackPlaceOrderClick();
    if (!cart?.orderReference) {
      await authorizeKlarna();
    } else {
      await submitOrder();
    }
  };

  useEffect(() => {
    if (authorizeToken) {
      submitOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizeToken]);

  useEffect(() => {
    setKlarnaDisabled(cart?.orderReference ? false : disabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  return (
    <>
      {showProcessingOrderModal && <ProcessingOrderModal />}
      <StyledCheckoutSubmitButton
        variant="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await submitPayment();
          }
        }}
        type="submit"
        data-testid="klarna-checkout-button"
        disabled={klarnaDisabled}
        isSubmitting={klarnaLoading}
      >
        {children}
      </StyledCheckoutSubmitButton>
    </>
  );
};

export default KlarnaButton;
